import * as GlobalTypes from "../types";
import { safeJsonParse } from './Json';

export declare type MultiCallback = (errors?: readonly (Error | null)[] | null) => void;
export declare type KeyValuePair = [string, string | null];
export declare type MultiGetCallback = (errors?: readonly (Error | null)[] | null, result?: readonly KeyValuePair[]) => void;

interface IPersistency {
    setItem(key: string, value: string, callback?: GlobalTypes.Callback):Promise<void>;
    getItem(key: string, callback?: GlobalTypes.CallbackWithResult<string>):Promise<string>;
    getAllKeys(callback?: GlobalTypes.CallbackWithResult<string[]>):Promise<string[]>;
    multiRemove(keys: readonly string[], callback?: MultiCallback):Promise<void>;
    multiGet(keys: readonly string[], callback?: MultiGetCallback):Promise<readonly KeyValuePair[]>;
    removeItem(key: string, callback?: GlobalTypes.Callback):Promise<void>;
}
interface IToaster {
    toastPlain(...args);
    toastIcon(...args);
    toastCustom(...args);
    toastClear(...args);
}

let persistency:IPersistency = null;
const defaultLogLevel = 'info';
let logLevel = 'info';
let deviceName = "poolside"
const logLevels = {
    verbose: 5,
    debug: 4,
    info: 3,
    warn: 2,
    error: 1,
    fatal: 0,
    highlight: -1 // Never leave logger.higlight() in the code! It is only for temporarily highlight a line while debugging/developing something.
};
const levelColors = {
    verbose: 'lightblack',
    debug: 'lightblack',
    warn: 'yellow',
    error: 'red',
    fatal: 'red',
    highlight: 'magenta'
}
const isBrowser = global?.location?.pathname ? true : false;

const logHistory = [];
const logHistoryMaxLength = 1000;
const errorHistory = [];
const errorHistoryMaxLength = 1000;
/**
 * "log_/isostring/" 
 * Opening a new log file for each app session ensures that restarting the app after a crash won't rotate out logs prior to the app crash.
 */
const logSessionId = 'log_' + (new Date().toISOString());

let isToastingLogsEnabled = true;
let isElevatedToastingLogsEnabled = false;
let toaster:IToaster = null;
let toastStyle = {};
let elevatedToastStyle = {
    borderStyle: 'dashed',
    borderWidth: 2
};

function colorize(text, color) {
    switch (color) {
    case 'black': return `[30m${text}[0m`;
    case 'lightblack': return `[90m${text}[0m`;
    case 'red': return `[31m${text}[0m`;
    case 'green': return `[32m${text}[0m`;
    case 'yellow': return `[33m${text}[0m`;
    case 'blue': return `[34m${text}[0m`;
    case 'magenta': return `[35m${text}[0m`;
    case 'cyan': return `[36m${text}[0m`;
    case 'white': return `[37${text}[0m`;
    default: return text;
    }
}

let storageTimer = null;
function addHistory(timestamp, level, msg) {
    logHistory.push({ timestamp, level, msg });
    if (logHistory.length > logHistoryMaxLength) {
        logHistory.shift();
    }

    if (persistency && process.env.NODE_ENV !== 'test') {
        // Throttle writing to the AsyncStorage
        clearTimeout(storageTimer);
        storageTimer = setTimeout(() => {
            persistency.setItem(logSessionId, JSON.stringify(logHistory))
                .catch(err => {
                    // Not using logger.warn() here to prevent infinity loop
                    console.warn('[logger] addHistory() AsyncStorage.setItem() error:', err);
                });
        }, 1000);
    }

    if (logLevels[level] <= logLevels['warn']) {
        errorHistory.push({ timestamp, level, msg });
        if (errorHistory.length > errorHistoryMaxLength) {
            errorHistory.shift();
        }
    }
}

/** Gets all entries from the in-memory log. */
function getInmemoryHistory() {
    return logHistory;
}

/** Gets all entries from the in-memory log. */
function getInmemoryErrorHistory() {
    return errorHistory;
}

/** Gets entries from the in-memory error log and also look up from archived logs
 * @param {Date} [from]
 * @returns {Promise<Array>}}
*/
function getErrorHistory(from) {
    if (!from) from = new Date(0);
    
    if (new Date(logSessionId.substr(4)) < from) {
        // In memory log started earlier than the requested from date so it should have the requested entries
        const log = errorHistory.filter(item => (new Date(item.timestamp)) >= from);
        return log;
    }
    
    return persistency ? 
        persistency.getAllKeys()
            .then(keys => {
                // Going backwards in log archives and keep adding them to toLoad until the start date of the last added one is before the from date
                // E.g: skip loading log4, using inmemory instead + log3 + log2. Log1 is not needed.
                //                                           |current timestamp
                //                 |from.....................|
                // |log1.....|log2.....|log3.....|log4.....|      (log4 misses some of the latest entries because of the delayed/throttled write)
                //                               |inmemory...|
                const toLoad = [];
                keys = keys.filter(item => item.indexOf('log_') === 0);
                keys = keys.filter(item => item !== logSessionId); // Inmemory log will be used instead of this archive. (Log entries appear delayed in the archive file)
                keys.sort();
                keys.reverse();
                keys.forEach(item => {
                    // `from` is before the start of inmemory / "log4" otherwise return would have been called earlier, see above.
                    // therefore the the next log in row "log3" should be added in any ways
                    if (!toLoad.length) {
                        toLoad.push(item);
                        return;
                    }
                    const oldestToLoad = new Date(toLoad[toLoad.length - 1].substr(4));
                    if (oldestToLoad > from) { // `from` is before the start date of the previously added log so next log in the row needs to be loaded too
                        toLoad.push(item);
                    }
                });
                return persistency.multiGet(toLoad);
            })
            .then(data => {
                let log = errorHistory;
                data.forEach(item => {
                    if (item[1]) {
                        const decoded = safeJsonParse(item[1]).filter(item => [ 'warn', 'error' ].includes(item.level));
                        log.unshift(...decoded);
                    }
                })
                return log.filter(item => (new Date(item.timestamp)) >= from);
            }) : [];
}

function stringify (format, ...args) {
    let msg = '';

    for (let i of args) {
        if (msg) msg += ' ';

        if (typeof i === 'undefined') {
            msg += 'undefined';
        } else if (i === null) {
            msg += 'null';
        } else if (typeof i === 'number') {
            msg += i.toString();
        } else if (typeof i === 'string') {
            msg += i;
        } else if (typeof i === 'boolean') {
            msg += i ? 'true' : 'false';
        } else if (i.stack) { // Error
            // i.stack does not use sourcemap but still, used to be more useful than console.log(i)
            // E.g it has the name of the funtion that actually throwed the error even if remote debugging was turned off.
            msg += i.toString() + '\n'
                + Object.entries(i).reduce((prev, curr) => prev + curr[0] + ': ' + stringify(format, curr[1]) + '\n', '')
                + 'stack:\n' + i.stack
            // Uncomment if remote debugging is turned on cause in debugging mode console.log() provides better stack trace
            // PS: stack trace only looks better inside the debugger not in the log output in console.
            // console.log(i);
        } else if (i instanceof Promise) {
            msg += 'Promise';
        } else if (typeof i === 'function') {
            msg += 'Function';
        } else {
            try {
                msg += JSON.stringify(i, (key, val) => {
                    if (typeof val === 'function') {
                        return '/Function/';
                    } else if (typeof val === 'undefined') {
                        return '/undefined/';
                    }
                    return val;
                }, format ? 4 : undefined);
            } catch (e) {
                msg += i;
            }
        }
    }

    msg = msg.replace(/(password|accessToken|refreshToken)([\",\']):([\",\']).*?([\",\'])/gi, '$1$2:$3********$4');
    return msg;
}

const logger = (level = 'info', ...args) => {
    if (logLevel && logLevels[level] > logLevels[logLevel]) { 
        return;
    }

    if (level === 'highlight') args.unshift('-------->');
    
    const date = new Date();
    const timestamp = date.getFullYear() + '-'
        + (date.getMonth() + 1).toString().padStart(2, '0') + '-'
        + date.getDate().toString().padStart(2, '0')
        + ' '
        + date.getHours().toString().padStart(2, '0') + ':'
        + date.getMinutes().toString().padStart(2, '0') + ':'
        + date.getSeconds().toString().padStart(2, '0')
    ;

    let msg = stringify(level === 'highlight', ...args);
    
    if ([ 'warn', 'error' ].includes(level)) {
        if (isElevatedToastingLogsEnabled) {
            toaster.toastIcon(
                level,
                msg.substr(0, 80) + (msg.length > 80 ? '…' : ''),
                elevatedToastStyle
            );
        }
    }
    addHistory(date.toISOString(), level, msg);

    let msgToLog = `${timestamp} [${deviceName}] ${msg}`;
    // Indenting multiline logs
    msgToLog = msgToLog.split('\n').map((item, index) => {
        if (index > 0) {
            item = '    ' + item;
        }
        return item;
    }).join('\n');

    if (isBrowser) {
        let method = [ 'debug', 'info', 'warn', 'error', 'log' ].includes(level) ? level :
            [ 'fatal' ].includes(level) ? 'error' :
                'log';
        // If remote debugger is attached, skip flattening / stringifying passed in args.
        // Allows better var inspection in the remote debugger console.
        // Note: switch to 'verbose' mode in the browser in order to see output of console.debug()
        console[method].apply(console, args);
        return;
    } else {
        //if (__DEV__) {
            console.log(colorize(msgToLog, levelColors[level]));
        //}
    }
}

function setPersistency(pers) {
    persistency = pers;
}

function setDeviceName(devName) {
    deviceName = devName;
}
async function setLogLevel(level, isPermanent = false) {
    if (level && typeof logLevels[level] === 'undefined') {
        logger('error', '[logger] setLogLevel() invalid level:', level);
        throw new Error('Unknown log level: ' + level);
    }

    if (level) {
        logLevel = level;
        if (isPermanent) {
            await persistency.setItem('logLevel', level)
                .then(() => {
                    logger('debug', `[logger] Log level saved in AsyncStorage. LogLevel: ${level}.`);
                })
                .catch(err => {
                    logger('error', `[logger] Failed to save log level in AsyncStorage. LogLevel: ${level},`, err);
                    throw err;
                })
        }
        logger('info', '[logger] Log level set to:', level, ', isPermanent:', isPermanent);
    } else {
        logLevel = defaultLogLevel;
        if (isPermanent) {
            await persistency.removeItem('logLevel')
                .then(() => {
                    logger('debug', '[logger] Log level cleared from AsyncStorage.');
                })
                .catch(err => {
                    logger('error', '[logger] Failed to clear log level from AsyncStorage:', err);
                    throw err;
                })
        }
        logger('info', '[logger] Log level set to default:', logLevel, ', isPermanent:', isPermanent);
    }
}

function setToastingEnabled(isEnabled, style, elevatedStyle) {
    isToastingLogsEnabled = isEnabled;
    if (style)
        toastStyle = style;
    if (elevatedStyle)
        elevatedToastStyle = elevatedStyle;
}

async function loadPersistedEnabled() {
    if (!persistency) {
        throw new Error('Persistency component is not set');
    }
    persistency.getItem('logLevel')
        .then(res => {
            if (res) {
                logger('info', '[logger] Overriding config logLevel:', defaultLogLevel, 'by value of AsyncStorage:', res);
                logLevel = res;
            }
        })
        .catch(err => {
            console.error('[logger] Could not get logLevel from AsyncStorage:', err);
        });
}

const verbose = logger.bind(logger, 'verbose');
const debug = logger.bind(logger, 'debug');
const info = logger.bind(logger, 'info');
const warn = logger.bind(logger, 'warn');
const error = logger.bind(logger, 'error');
const fatal = logger.bind(logger, 'fatal');
const highlight = logger.bind(logger, 'highlight');
const hl = logger.bind(logger, 'highlight');

export default {
    verbose, debug, info, warn, error, fatal, highlight, hl,
    logLevel: logLevels[logLevel],
    logLevels,
    setLogLevel, setToastingEnabled, setDeviceName, setPersistency, loadPersistedEnabled,
    logHistoryMaxLength, getErrorHistory, getInmemoryErrorHistory,
    errorHistoryMaxLength, getInmemoryHistory, logSessionId
};